import "./app.css";
import React, { createContext, useMemo } from "react";
// Begin React Router
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
// End React Router
// Begin Mantine
import { MantineProvider, AppShell, rem, LoadingOverlay } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { useHeadroom } from "@mantine/hooks";
import { theme, variableResolver } from "./util/mantineTheme";
// End Mantine
// Begin Providers
import { GoogleOAuthProvider } from "@react-oauth/google";
import {
  CurrentUserProvider,
  CurrentUserUpdater,
} from "./contexts/currentUserContext";
import { CampaignProvider, CampaignUpdater } from "./contexts/campaignContext";
import appAxios from "./services/axios";
import { useCurrentUser } from "./hooks/currentUser";
// End Providers
// Begin Wrappers
import Nav from "./components/nav/Nav";
import NotFound from "./components/not-found/NotFound";
import Backpannel from "./components/Backpannel";
import styled from "styled-components";
// End Wrappers
// Begin Pages
import Login from "./components/login/Login";
import Main from "./components/dashboard/Main";
import CampaignRoutes from "./components/campaigns/Routes";
import SettingsRoutes from "./components/settings/Routes";
import WeddingRoutes from "./components/wedding/Routes";
// End Pages

const StyledContentArea = styled.div`
  padding-top: 20px;
  min-height: 100vh;
`;

const StyledContent = styled.div`
  padding-left: 20px;
  padding-right: 20px;
`;

export const AppContext = createContext();

const AppHeader = React.memo(() => (
  <AppShell.Header>
    <Nav />
  </AppShell.Header>
));

const AppMain = React.memo(() => (
  <AppShell.Main mt={`calc(${rem(30)})`}>
    <StyledContentArea>
      <StyledContent>
        <Outlet />
      </StyledContent>
    </StyledContentArea>
  </AppShell.Main>
));

const AppContainerWrapper = () => {
  const headerHeight = 50;
  const pinned = useHeadroom({ fixedAt: headerHeight });

  const appShellProps = useMemo(
    () => ({
      header: { height: headerHeight, collapsed: !pinned, offset: false },
    }),
    [headerHeight, pinned],
  );
  // Google sign-in lives here, not at the app root, so wedding guests never load Google's script.
  return (
    <GoogleOAuthProvider clientId="405075599114-d4rtlc1d2jds1tovtsbscaguomg73aq5.apps.googleusercontent.com">
      <MantineProvider theme={theme} cssVariablesResolver={variableResolver}>
        <AppShell {...appShellProps}>
          <AppHeader />
          <Backpannel>
            <AppMain />
            <Notifications position="bottom-right" />
            <CampaignUpdater />
            <CurrentUserUpdater />
          </Backpannel>
        </AppShell>
      </MantineProvider>
    </GoogleOAuthProvider>
  );
};

const AuthenticatedRoute = () => {
  const { currentUser } = useCurrentUser();
  const location = useLocation();
  if (!currentUser) {
    return (
      <LoadingOverlay
        visible={!currentUser}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
    );
  }
  // check if user is authenticated
  return currentUser.isAuth ? (
    <Outlet />
  ) : (
    <Navigate to={`/login?referer=${location.pathname}`} />
  );
};

const App = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* The wedding pages sit outside the D&D shell: no nav, backgrounds or sign-in */}
        {WeddingRoutes()}
        <Route element={<AppContainerWrapper />}>
          {/* Anything under a Route that has an AuthenticatedRoute element requires auth on all children routes */}
          <Route element={<AuthenticatedRoute />}>
            <Route path="/" element={<Main />} />
            {CampaignRoutes()}
            {SettingsRoutes()}
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </>,
    ),
  );
  return (
    <AppContext.Provider value={{ appAxios }}>
      <CurrentUserProvider>
        <CampaignProvider>
          <RouterProvider router={router} />
        </CampaignProvider>
      </CurrentUserProvider>
    </AppContext.Provider>
  );
};

export default App;
